import { Component, HostBinding, Input } from '@angular/core';
import { isNil } from 'lodash';

export namespace ServiceStatusIconComponent {
  export type ServiceStatusInfo = {
    active: string;
    status: string;
  };

  export type ServiceStatus = 'active' | 'warn' | 'error' | 'inactive' | 'unknown';
}

export function simplifyStatus(service?: ServiceStatusIconComponent.ServiceStatusInfo) {
  // active === 'failed' is a real failure
  if (service?.active === 'failed') return 'error';

  switch (service?.status ?? '') {
    case 'running':
    case 'active':
      return 'active';
    case 'plugged':
    case 'mounted':
    case 'waiting':
    case 'listening':
      return 'warn';
    case 'abandoned':
    case 'exited':
      return 'error';
    case 'dead':
      // inactive + dead = intentionally stopped, not a failure
      return service?.active === 'inactive' ? 'inactive' : 'error';
    default:
      return 'unknown';
  }
}

export function consolidateStatus(services?: ServiceStatusIconComponent.ServiceStatusInfo[]) {
  if (isNil(services) || services.length === 0) return 'unknown';
  const statuses = services.map(simplifyStatus);
  // Priority: error > warn > inactive > active
  if (statuses.includes('error')) return 'error';
  if (statuses.includes('warn')) return 'warn';
  if (statuses.includes('inactive')) return 'inactive';
  return 'active';
}

export function getStatusIcon(statusClass?: string) {
  switch (statusClass) {
    case 'active':
      return 'directions_running';
    case 'warn':
      return 'priority_high';
    case 'error':
      return 'close';
    case 'inactive':
      return 'power_settings_new';
    case 'unknown':
    default:
      return 'question_mark';
  }
}

export function getStatusTooltip(statusClass?: string) {
  switch (statusClass) {
    case 'active':
      return 'All services running';
    case 'warn':
      return 'One or more services pending';
    case 'error':
      return 'One or more services failed';
    case 'inactive':
      return 'Service stopped';
    case 'unknown':
    default:
      return 'No services available';
  }
}

@Component({
    selector: 'app-service-status-icon',
    templateUrl: './service-status-icon.component.html',
    styleUrls: ['./service-status-icon.component.scss'],
    standalone: false
})
export default class ServiceStatusIconComponent {
  @HostBinding('class')
  @Input()
  status: ServiceStatusIconComponent.ServiceStatus;

  @Input()
  icon: string | undefined | null = undefined;

  @Input()
  tooltip: boolean | undefined = undefined;

  public get renderedIcon() {
    return !isNil(this.icon) ? this.icon : getStatusIcon(this.status);
  }

  public get renderedTooltip() {
    return getStatusTooltip(this.status);
  }

  public simplifyStatus(service?: ServiceStatusIconComponent.ServiceStatusInfo) {
    return simplifyStatus(service);
  }
}
